import React, { ElementType, Suspense, useCallback, useState } from 'react';

import { Box } from '@rbilabs/component-library';
import delve from 'dlv';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import CreditCardFormInputs from 'components/credit-card-form-inputs';
import IconCheckCircle from 'components/icons/check-circle';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import PaymentMethodSelect, { HoverTheme } from 'components/payment-method-select';
import { useAddPrepaidCardMutation } from 'generated/rbi-graphql';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { useAuthContext } from 'state/auth';
import { useFraudPreventionContext } from 'state/fraud-prevention';
import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import useGooglePay from 'state/google-pay/hooks/use-google-pay';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { useLogger } from 'state/logger';
import { usePaymentContext } from 'state/payment';
import { CardTypes, IApplePayDetails, IGooglePayDetails } from 'state/payment/types';
import { useUIContext } from 'state/ui';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import {
  initialErrorState,
  initialPaymentState,
  onCCFormChange,
  testCardPaymentState,
} from 'utils/payment';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';

import { messages } from './messages';
import {
  AnimatedArrow,
  CenteredContent,
  CollapsibleContent,
  DisplayToggle,
  ErrorAnchor,
  FormSection,
  InstructionText,
  LeftAlignedContent,
  NormalText,
  PlacementHolder,
  SectionDescription,
  SectionHeading,
  SpacedContent,
  SubInstructionText,
  SubheadingButton,
  SubheadingContainer,
  SuccessHeading,
  TimCardText,
} from './styled';
import { IAddPrepaidCardArgs, ILoadPrepaidCardModal } from './types';
import { prepaidCardMapInput } from './utils';

enum ModalStates {
  FORM,
  SUCCESS,
}

const LoadPrepaidCardModal = ({
  onDismiss,
  loadAmount,
  modalAppearanceEventMessage = 'Order Cancelled',
}: ILoadPrepaidCardModal) => {
  const { kountSessionId, resetKountSessionId } = useFraudPreventionContext();
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const {
    addPaymentMethod,
    getPrepaidPaymentMethod,
    getPaymentMethods,
    paymentMethods,
    defaultReloadPaymentMethodId: reloadPaymentMethodId,
    setDefaultReloadPaymentMethodId: setReloadPaymentMethodId,
    setDefaultPaymentMethodId,
    canUseApplePay,
    transformPaymentValues,
  } = usePaymentContext();
  const [addPrepaidCardMutation] = useAddPrepaidCardMutation({
    onCompleted: resetKountSessionId,
    onError: resetKountSessionId,
  });
  const auth = useAuthContext();
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Prepaid Account Creation Failure',
  });

  const billingCountry = delve(auth, 'user.details.isoCountryCode') || ISOs.CAN;
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);

  const logger = useLogger();
  const { formatMessage } = useIntl();
  const { formatCurrencyForUser } = useUIContext();

  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);

  const [loading, setLoading] = useState(false);
  const [prepaidCardBalance, setPrepaidCardBalance] = useState(0);
  const [errors, setErrors] = useState(initialErrorState);
  const userDetailsName = auth.user?.details.name;
  const [paymentValues, setPaymentValues] = useState(
    initialPaymentState({ billingCountry: ISOs.CAN, userDetailsName, paymentFieldVariations })
  );
  const [section, setSection] = useState<ModalStates>(ModalStates.FORM);

  const handleApplePay = ({ error, data }: { error?: string; data?: IApplePayDetails }): void => {
    if (data) {
      addNewPrepaidAccount(data);
      return;
    }
    error && logger.warn(error);
    setLoading(false);
  };

  const { requestApplePayPayment } = useApplePay({
    handleApplePay,
    prepaid: true,
  });

  const { requestGooglePayPayment } = useGooglePay({
    onError: () => {
      setLoading(false);
    },
    onCompleted: (data: IGooglePayDetails) => {
      if (!data) {
        setLoading(false);
        return;
      }
      addNewPrepaidAccount(data);
    },
  });

  const [showAddNewPaymentMethodForm, setShowAddNewPaymentMethodForm] = useState(
    !canUseApplePay && paymentMethods.length === 0
  );

  const allowedReloadPaymentMethods = paymentMethods.filter(
    paymentMethod =>
      !paymentMethod.prepaid &&
      (!reloadPaymentMethodId || reloadPaymentMethodId !== paymentMethod.fdAccountId)
  );
  const showExistingCardSection = allowedReloadPaymentMethods.length > 0 || !!reloadPaymentMethodId;
  const reloadPaymentMethod = paymentMethods.find(
    method => method.fdAccountId === reloadPaymentMethodId
  );

  useEffectOnce(() => {
    if (autoFill) {
      setPaymentValues(testCardPaymentState({ paymentFieldVariations }));
    }
  });

  const displayAddPaymentMethodForm = useCallback(
    (bool: boolean) => () => setShowAddNewPaymentMethodForm(bool),
    []
  );

  const applePaySelected = isApplePay(reloadPaymentMethodId);
  const googlePaySelected = isGooglePay(reloadPaymentMethodId);

  const handlePaymentMethodOptionClick = (fdAccountId: string) => {
    // If apple pay is available in the client, append it to payment methods
    const paymentMethodToLoadWith = paymentMethods.find(paymentMethod => {
      const accountIdentifier = paymentMethod.fdAccountId ?? paymentMethod.accountIdentifier ?? '';
      return accountIdentifier === fdAccountId;
    });
    if (paymentMethodToLoadWith) {
      setReloadPaymentMethodId(
        paymentMethodToLoadWith.fdAccountId ?? paymentMethodToLoadWith.accountIdentifier ?? ''
      );
    }
  };

  const handleChange = useCallback(
    (name, value) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        paymentValues,
        errors,
        formatMessage,
        // @TODO: pull supportedCardTypes from Sanity
        ['AMEX', 'DINERS_CLUB', 'DISCOVER', 'JCB', 'MASTERCARD', 'VISA']
      );
      setErrors(prevState => ({ ...prevState, ...formErrors }));
      setPaymentValues(prevState => ({ ...prevState, ...state }));
    },
    [paymentValues, errors, formatMessage]
  );

  const maybeAddPaymentMethod = useCallback(() => {
    if (showAddNewPaymentMethodForm) {
      return addPaymentMethod(transformPaymentValues(paymentValues), {
        // Skip the default error dialog since we are showing a better one here
        skipErrorDialogOnError: true,
      });
    }
    return Promise.resolve(reloadPaymentMethodId);
  }, [
    addPaymentMethod,
    paymentValues,
    reloadPaymentMethodId,
    showAddNewPaymentMethodForm,
    transformPaymentValues,
  ]);

  const errorText = formatMessage(messages.addGiftCardError, {
    link: (
      <ErrorAnchor href="https://timchat.timhortons.com/feedback/">
        {formatMessage(messages.chatWithUs)}
      </ErrorAnchor>
    ),
  });

  const addNewPrepaidAccount = useCallback(
    async (input?: IApplePayDetails | IGooglePayDetails): Promise<void> => {
      try {
        const fdAccountId = await maybeAddPaymentMethod();
        const args: IAddPrepaidCardArgs = { fundValue: loadAmount, kountSessionId };

        switch (reloadPaymentMethodId) {
          case CardTypes.APPLE_PAY:
            Object.assign(args, { applePayDetails: input });
            break;
          case CardTypes.GOOGLE_PAY:
            Object.assign(args, { googlePayDetails: input });
            break;
          default: {
            // users are somehow getting to this point without one of the required inputs
            // throwing here to stop them from continuing on to the addPrepaidCardMutation that will inveitably fail
            if (!fdAccountId) {
              logger.error(
                `Cannot add prepaid card with the following fdAccountId - ${fdAccountId}`
              );
              throw new Error();
            }
            Object.assign(args, { fdAccountId });
          }
        }

        if (getPrepaidPaymentMethod()) {
          openErrorDialog({
            message: formatMessage(messages.prepaidAccountAlreadyExistsError),
          });
          return;
        }

        const response = await addPrepaidCardMutation({
          variables: { input: prepaidCardMapInput(args) },
        });
        const { currentBalance } = delve(response, 'data.addGiftAccount.prepaid', {});
        setPrepaidCardBalance(currentBalance);
        setSection(ModalStates.SUCCESS);
        // Set the checkout payment method to the prepaid card fdAccountId coming from the response
        setDefaultPaymentMethodId(response.data?.addGiftAccount?.fdAccountId || '');
        getPaymentMethods();
      } catch (error) {
        openErrorDialog({
          message: errorText as ElementType,
          error,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      maybeAddPaymentMethod,
      loadAmount,
      kountSessionId,
      reloadPaymentMethodId,
      getPrepaidPaymentMethod,
      addPrepaidCardMutation,
      setDefaultPaymentMethodId,
      getPaymentMethods,
      logger,
      openErrorDialog,
      formatMessage,
      errorText,
    ]
  );

  const handleSubmit = useCallback(() => {
    setLoading(true);
    if (applePaySelected && !showAddNewPaymentMethodForm) {
      return requestApplePayPayment({
        countryCode,
        currencyCode,
        total: loadAmount,
      });
    }
    if (googlePaySelected && !showAddNewPaymentMethodForm) {
      // Get the prepaid gateway configs for google pay
      const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
        transactionType: TransactionType.PREPAID,
        countryCode,
      });
      return requestGooglePayPayment({
        countryCode,
        currencyCode,
        total: loadAmount,
        gatewayMerchantId,
        gateway,
      });
    }
    addNewPrepaidAccount();
  }, [
    applePaySelected,
    googlePaySelected,
    showAddNewPaymentMethodForm,
    addNewPrepaidAccount,
    requestApplePayPayment,
    requestGooglePayPayment,
    countryCode,
    currencyCode,
    loadAmount,
  ]);

  // disable the button if:
  // - The form is hidden and no reload method is selected
  const disabled = !showAddNewPaymentMethodForm && !reloadPaymentMethodId;
  const modalHeading = formatMessage(messages.loadYourTimCard);

  return (
    <Modal
      onDismiss={onDismiss}
      mParticleEventData={{
        modalAppearanceEventMessage,
      }}
    >
      <ModalContent>
        <ModalHeading>{modalHeading}</ModalHeading>
        {section === ModalStates.FORM && (
          <>
            <div>
              <SectionHeading>{formatMessage(messages.welcomeToYour)}</SectionHeading>
              <TimCardText>{formatMessage(messages.emptyStateTimCardHeadline)}</TimCardText>
            </div>
            <InstructionText>{formatMessage(messages.timCardInstructions)}</InstructionText>
            <SubInstructionText>
              {formatMessage(messages.letsLoadX, {
                amount: formatCurrencyForUser(loadAmount),
              })}
            </SubInstructionText>

            <SpacedContent>
              <LeftAlignedContent>
                <SubheadingContainer>
                  <SubheadingButton onClick={displayAddPaymentMethodForm(true)}>
                    {formatMessage(messages.paymentMethod)}
                  </SubheadingButton>
                  <DisplayToggle>
                    <AnimatedArrow collapsed={!showAddNewPaymentMethodForm} />
                  </DisplayToggle>
                </SubheadingContainer>
              </LeftAlignedContent>
              <CollapsibleContent collapsed={!showAddNewPaymentMethodForm}>
                <FormSection>
                  <CreditCardFormInputs
                    onChange={handleChange}
                    paymentValues={paymentValues}
                    errors={errors}
                    showSaveCard={false}
                  />
                </FormSection>
              </CollapsibleContent>
            </SpacedContent>

            {(showExistingCardSection || canUseApplePay) && (
              <SpacedContent>
                <LeftAlignedContent>
                  <SubheadingContainer>
                    <SubheadingButton onClick={displayAddPaymentMethodForm(false)}>
                      {formatMessage(messages.selectPaymentMethod)}
                    </SubheadingButton>
                    <DisplayToggle>
                      <AnimatedArrow collapsed={showAddNewPaymentMethodForm} />
                    </DisplayToggle>
                  </SubheadingContainer>
                </LeftAlignedContent>
                <CollapsibleContent collapsed={showAddNewPaymentMethodForm}>
                  <>
                    <SectionDescription>
                      {formatMessage(messages.selectPaymentMethodToAddFunds)}
                    </SectionDescription>
                    <PaymentMethodSelect
                      selectedMethod={reloadPaymentMethod}
                      nonSelectedMethods={allowedReloadPaymentMethods}
                      onOptionClick={handlePaymentMethodOptionClick}
                      hoverTheme={HoverTheme.LIGHT}
                    />
                  </>
                </CollapsibleContent>
              </SpacedContent>
            )}

            <Box width="100%" center>
              <ActionButton
                isLoading={loading}
                disabled={disabled}
                onClick={handleSubmit}
                data-testid="load-timcard-submit"
              >
                {formatMessage(messages.loadXOnTimCard, {
                  amount: formatCurrencyForUser(loadAmount),
                })}
              </ActionButton>
            </Box>
          </>
        )}
        <ErrorDialog />
        {section === ModalStates.SUCCESS && (
          <CenteredContent>
            <Suspense fallback={<PlacementHolder height="57px" width="57px" />}>
              <IconCheckCircle width="57" height="57" />
            </Suspense>
            <SuccessHeading data-testid="success">{formatMessage(messages.success)}</SuccessHeading>
            <NormalText>
              {formatMessage(messages.yourNewBalanceX, {
                newBalance: formatCurrencyForUser(prepaidCardBalance),
              })}
            </NormalText>
          </CenteredContent>
        )}
      </ModalContent>
    </Modal>
  );
};

export default LoadPrepaidCardModal;
